import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { Stack, Text } from '@chakra-ui/react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { createStore } from 'effector';
import { Country } from 'react-phone-number-input';

import { Button } from '@m/components-ui/button';
import { HCAPTCHA_SITE_KEY } from '@m/config/env';
import { useCustomerFeatureFlag } from '@m/hooks/data/featureFlags';
import { requestCode } from 'apps/customer/state/user';
import {
  getCallingCode,
  getPhoneNumberCountry,
  isInternationalCallingCode,
} from 'apps/customer/utils/phoneCountry';
import {
  trackCaptchaAbandoned,
  trackCaptchaError,
  trackCaptchaExpired,
  trackCaptchaRetryClicked,
  trackCaptchaShown,
  trackCaptchaSolved,
  trackCaptchaUnavailable,
  trackResendClicked,
  type CaptchaTrigger,
  type PhoneVerificationSource,
} from 'apps/customer/utils/phoneVerificationHeap';

/**
 * site-api's OTP gate requires an hCaptcha token for every country except +1 (US/Canada)
 * while this flag is on (the default of the server's `hcaptcha.allowlistCountryCodes`).
 */
const HCAPTCHA_FLAG = 'HcaptchaEnabled';

/** Widget errors the user recovers from by just ticking the box again. */
const SELF_RECOVERING_ERRORS = new Set(['challenge-closed', 'challenge-expired']);

/**
 * Calling codes the server has answered with HCAPTCHA_REQUIRED. The server is the source of
 * truth: if the flag hasn't loaded yet, or the server's flag or allowlist disagrees with ours,
 * show the captcha anyway so the user isn't stuck re-submitting.
 */
const $serverRequiredCallingCodes = createStore<string[]>([]).on(
  requestCode.done,
  (codes, { params, result }) => {
    const callingCode = getCallingCode(getPhoneNumberCountry(params.phoneNumber));
    if (!callingCode || codes.includes(callingCode)) return codes;
    return result.error?.includes('HCAPTCHA_REQUIRED') ? [...codes, callingCode] : codes;
  },
);

// Read outside effector-react: this gate sits inside many screens whose tests mock `useUnit` for
// their own stores.
const subscribeToServerRequired = (onChange: () => void) =>
  $serverRequiredCallingCodes.watch(onChange);
const getServerRequiredCallingCodes = () => $serverRequiredCallingCodes.getState();
const useServerRequiredCallingCodes = () =>
  useSyncExternalStore(subscribeToServerRequired, getServerRequiredCallingCodes);

export function useHcaptchaEnabled() {
  const { data: enabled = false } = useCustomerFeatureFlag(HCAPTCHA_FLAG);
  const serverRequiredCallingCodes = useServerRequiredCallingCodes();
  return enabled || serverRequiredCallingCodes.length > 0;
}

/**
 * False while the flag is still being fetched. A send that fires on mount must wait for this, or it
 * reads the flag as off and goes out without the captcha the server will demand.
 */
export function useHcaptchaFlagSettled() {
  // isLoading (not isPending): a query that never runs stays pending forever but is settled.
  const { isLoading } = useCustomerFeatureFlag(HCAPTCHA_FLAG);
  return !isLoading;
}

function getWidgetErrorCode(event: unknown) {
  if (typeof event === 'string') return event;
  // A script that fails to load arrives as an Error rather than the typed string.
  return event instanceof Error ? event.message : 'unknown';
}

type UseHcaptchaGateOptions = {
  enabled: boolean;
  /** Called with each solved token, e.g. to clear a stale submit error. */
  onVerify?: (token: string) => void;
  selectedCountry: Country | undefined;
  source: PhoneVerificationSource;
  trigger?: CaptchaTrigger;
  /** False keeps the widget unmounted even when a captcha is required. */
  visible?: boolean;
};

/**
 * Owns the hCaptcha widget for a phone-entry form: whether it is required for the selected
 * country, the current token, and resetting it. Tokens are single-use, so call
 * `resetAfterSubmit` once each verification-code request settles.
 */
export function useHcaptchaGate({
  enabled,
  onVerify,
  selectedCountry,
  source,
  trigger = 'Send',
  visible = true,
}: UseHcaptchaGateOptions) {
  const [hcaptchaToken, setHcaptchaToken] = useState<string | null>(null);
  const [widgetError, setWidgetError] = useState<string | null>(null);
  const [widgetKey, setWidgetKey] = useState(0);
  const hcaptchaRef = useRef<HCaptcha | null>(null);
  const shownAtRef = useRef<number | null>(null);
  const serverRequiredCallingCodes = useServerRequiredCallingCodes();

  const callingCode = getCallingCode(selectedCountry);
  const countryGated =
    callingCode !== undefined &&
    (isInternationalCallingCode(callingCode) || serverRequiredCallingCodes.includes(callingCode));
  const captchaRequired = enabled && Boolean(HCAPTCHA_SITE_KEY) && countryGated;
  const isShown = captchaRequired && visible;
  const context = { country: selectedCountry, source, trigger };

  useEffect(() => {
    // Avoid reusing a token across calling-code / captcha toggle changes. Keyed on the calling
    // code, not the country: a number can re-parse between countries that share one (GB/JE/GG)
    // as it's typed, and that must not throw away a solved captcha.
    setHcaptchaToken(null);
    setWidgetError(null);
    if (captchaRequired) {
      hcaptchaRef.current?.resetCaptcha();
    }
  }, [captchaRequired, callingCode]);

  // Kept out of the effect below so switching between two gated countries isn't a new showing.
  const countryRef = useRef(selectedCountry);
  const solvedRef = useRef(false);
  useEffect(() => {
    countryRef.current = selectedCountry;
  });

  useEffect(() => {
    if (!isShown) return undefined;
    const shownAt = Date.now();
    shownAtRef.current = shownAt;
    solvedRef.current = false;
    trackCaptchaShown({ country: countryRef.current, source, trigger });
    return () => {
      // Hidden or unmounted without a solve: the user left, or switched to a +1 country.
      if (solvedRef.current) return;
      trackCaptchaAbandoned({
        country: countryRef.current,
        secondsShown: Math.round((Date.now() - shownAt) / 1000),
        source,
        trigger,
      });
    };
  }, [isShown, source, trigger]);

  useEffect(() => {
    if (enabled && countryGated && visible && !HCAPTCHA_SITE_KEY) {
      trackCaptchaUnavailable({ country: selectedCountry, source, trigger });
    }
  }, [enabled, countryGated, visible, selectedCountry, source, trigger]);

  const resetAfterSubmit = useCallback(() => {
    if (captchaRequired) {
      hcaptchaRef.current?.resetCaptcha();
      setHcaptchaToken(null);
      // The reset widget is a fresh, unsolved challenge; leaving now is an abandonment.
      solvedRef.current = false;
    }
  }, [captchaRequired]);

  const retry = () => {
    trackCaptchaRetryClicked(context);
    setWidgetError(null);
    // Remounting re-requests the hCaptcha script, which is what fails behind blockers or bad networks.
    setWidgetKey((key) => key + 1);
  };

  const captcha =
    isShown && HCAPTCHA_SITE_KEY ? (
      <Stack gap="2" align="center" width="full" textAlign="center" data-testid="hcaptcha-gate">
        <HCaptcha
          key={widgetKey}
          ref={hcaptchaRef}
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={(token) => {
            const shownAt = shownAtRef.current;
            trackCaptchaSolved({
              ...context,
              secondsToSolve: shownAt ? Math.round((Date.now() - shownAt) / 1000) : undefined,
            });
            solvedRef.current = true;
            setWidgetError(null);
            onVerify?.(token);
            setHcaptchaToken(token);
          }}
          onError={(event: unknown) => {
            const error = getWidgetErrorCode(event);
            trackCaptchaError({ ...context, error });
            setHcaptchaToken(null);
            if (!SELF_RECOVERING_ERRORS.has(error)) setWidgetError(error);
          }}
          onExpire={() => {
            solvedRef.current = false;
            trackCaptchaExpired(context);
            setHcaptchaToken(null);
          }}
        />
        {widgetError && (
          <>
            <Text variant="SmallBodyRegular" color="text.error">
              {widgetError === 'script-error'
                ? "We couldn't load the verification check. Check your connection or turn off content blockers, then try again."
                : 'The verification check ran into a problem. Please try again.'}
            </Text>
            <Button variant="link" size="md" px={0} onClick={retry}>
              Try again
            </Button>
          </>
        )}
      </Stack>
    ) : null;

  return { captcha, captchaRequired, hcaptchaToken, resetAfterSubmit };
}

type UseHcaptchaResendOptions = {
  /** False for sends that bypass the captcha gate, e.g. the logged-in update-phone code. */
  enabled: boolean;
  /** A signed-in user verifying a new number, for tracking. */
  isPhoneChange?: boolean;
  phoneNumber?: string | null;
  resend: (hcaptchaToken?: string) => void;
  source: PhoneVerificationSource;
};

/** Shown above a resend captcha so nobody waits for a code that hasn't been sent. */
export const HCAPTCHA_RESEND_PROMPT =
  "We'll send your code once you complete the verification below.";

/**
 * Resending a code goes through the same captcha gate as the first send, and that token is
 * already spent. `requestResend` sends right away when no captcha is needed; otherwise it reveals
 * `captcha`, with a prompt saying the code is held until it's solved, and sends once it is.
 */
export function useHcaptchaResend({
  enabled,
  isPhoneChange,
  phoneNumber,
  resend,
  source,
}: UseHcaptchaResendOptions) {
  const [awaitingCaptcha, setAwaitingCaptcha] = useState(false);
  const [trigger, setTrigger] = useState<CaptchaTrigger>('Resend');
  const { captcha, captchaRequired } = useHcaptchaGate({
    enabled,
    onVerify: (token) => {
      setAwaitingCaptcha(false);
      resend(token);
    },
    selectedCountry: getPhoneNumberCountry(phoneNumber),
    source,
    trigger,
    visible: awaitingCaptcha,
  });

  /**
   * Sends now, or shows the captcha and sends once it's solved. `isFirstSend` is for screens that
   * send automatically on open (no resend click), so it isn't tracked as one.
   */
  const requestResend = ({ isFirstSend = false }: { isFirstSend?: boolean } = {}) => {
    setTrigger(isFirstSend ? 'Send' : 'Resend');
    if (phoneNumber && !isFirstSend) {
      trackResendClicked({ captchaRequired, isPhoneChange, phoneNumber, source });
    }
    if (captchaRequired) setAwaitingCaptcha(true);
    else resend();
  };

  /** Drops a pending captcha, e.g. when the user leaves the code step without solving it. */
  const cancelResend = () => setAwaitingCaptcha(false);

  return {
    captcha: captcha && (
      <Stack gap="3" align="center" width="full" textAlign="center">
        <Text variant="SmallBodyRegular" color="text.secondary">
          {HCAPTCHA_RESEND_PROMPT}
        </Text>
        {captcha}
      </Stack>
    ),
    cancelResend,
    requestResend,
  };
}
